import type { AuditEntry } from '@lms/shared-types'
import { AiAssistedChip } from '../AiAssistedChip'
import { ConfigurePatternLink } from '../ConfigurePatternLink'
import { CategoryChip, ChangeDiffExpander } from './ChangeDiff'
import { formatTimestamp, humanizeSubjectType } from './audit-format'

const ROW_HEIGHT = 64

/** Resolved subject name, or a graceful fallback to type + short id. */
function subjectLine(entry: AuditEntry): string {
  if (entry.subjectLabel) return entry.subjectLabel
  const shortId = entry.subjectId ? ` · ${entry.subjectId.slice(0, 8)}` : ''
  return `${humanizeSubjectType(entry.subjectType)}${shortId}`
}

/**
 * One human-readable audit row: a plain-English label + category chip, a
 * resolved subject line, the actor, the source, and the time — with an
 * expandable field-level change diff. Reads as a trail
 * ("Aisha Rahman · Leave approved · Ben Tan · annual · 12–14 Aug").
 */
export function ReadableAuditRow({ entry }: { entry: AuditEntry }): JSX.Element {
  const aiProvenance = entry.aiProvenance
  const isAiAssisted = aiProvenance != null && aiProvenance.aiInvocationId != null

  const proseOrigin: 'ai' | 'ai_edited' | 'human' | null = (() => {
    if (!isAiAssisted) return null
    const meta = entry.metadata
    if (meta != null && typeof meta === 'object' && 'prose_origin' in meta) {
      const raw = (meta as Record<string, unknown>)['prose_origin']
      if (raw === 'ai' || raw === 'ai_edited' || raw === 'human') return raw
    }
    return null
  })()

  const patternCatalogCode: string | null = (() => {
    if (entry.action !== 'pattern_catalog.updated') return null
    const meta = entry.metadata
    if (meta != null && typeof meta === 'object' && 'code' in meta) {
      const raw = (meta as Record<string, unknown>)['code']
      if (typeof raw === 'string' && raw.length > 0) return raw
    }
    return null
  })()

  const label = entry.label || entry.action

  return (
    <li
      data-testid="audit-row"
      data-audit-id={entry.id}
      style={{
        minHeight: ROW_HEIGHT,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 200px 160px 140px',
        alignItems: 'center',
        gap: 12,
        padding: '8px 14px',
        borderBottom: '1px solid var(--french-gray-2)',
        fontSize: 13,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          {entry.category ? (
            <CategoryChip category={entry.category} label={entry.subjectKind} />
          ) : null}
          <span
            style={{ fontWeight: 600, color: 'var(--text)' }}
            data-testid="audit-label"
            title={entry.action}
          >
            {label}
          </span>
          {isAiAssisted ? (
            <AiAssistedChip
              modelId={aiProvenance.aiModelId ?? ''}
              promptVersion={aiProvenance.aiPromptVersion ?? ''}
              proseOrigin={proseOrigin}
            />
          ) : null}
          {patternCatalogCode ? <ConfigurePatternLink code={patternCatalogCode} /> : null}
          <ChangeDiffExpander changes={entry.changeSummary} />
        </div>
        <span
          data-testid="audit-subject"
          style={{
            fontSize: 12,
            color: 'var(--text-2)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subjectLine(entry)}
        </span>
      </div>
      <div style={{ minWidth: 0 }}>
        <span style={{ color: 'var(--text)' }}>
          {entry.actorName ?? (entry.source === 'system' ? 'System' : 'System')}
        </span>
        {entry.actorRole ? (
          <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-3)' }}>
            ({entry.actorRole})
          </span>
        ) : null}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'capitalize' }}>
        {entry.source}
      </div>
      <div
        style={{
          textAlign: 'right',
          color: 'var(--text-3)',
          fontSize: 12,
          fontFamily: 'var(--font-mono, monospace)',
        }}
      >
        {formatTimestamp(entry.createdAt)}
      </div>
    </li>
  )
}
